import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, Wine, Sparkles, ShoppingCart } from 'lucide-react';

const Wines = ({ onBookCall }) => {
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.indexOf(entry.target);
            if (index !== -1 && !visibleSections.includes(index)) {
              setVisibleSections((prev) => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const categories = [
    {
      name: 'Bronze',
      price: '$150',
      descriptor: 'Easy Drinking',
      subtitle: 'Light, approachable, everyday wines',
      image: 'https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/5tmnbzni_Website%20Wedding%20Pictures%20%283%29.svg',
      theme: 'from-amber-700/20 to-amber-900/10',
      accentColor: 'border-amber-700/30',
      badgeColor: 'bg-amber-700',
      whites: [
        'Pinot Grigio',
        'Sauvignon Blanc',
        'Chardonnay',
        'Riesling',
        'California White'
      ],
      reds: [
        'Pinot Noir',
        'Cabernet Sauvignon',
        'Vieux Chateau du Roi',
        'Shiraz',
        'Merlot'
      ]
    },
    {
      name: 'Silver',
      price: '$182',
      descriptor: 'Premium Selection',
      subtitle: 'Balanced, crowd-pleasing favourites',
      image: 'https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/67rmbcug_Website%20Wedding%20Pictures%20%284%29.svg',
      theme: 'from-gray-400/20 to-gray-600/10',
      accentColor: 'border-gray-500/30',
      badgeColor: 'bg-gray-500',
      whites: [
        'California Muscat O/D',
        'Germany Riesling O/D',
        'South Africa Chenin Blanc',
        'California Chardonnay',
        'California White Zinfandel',
        'Germany Gewürztraminer O/D',
        'Italy Verdicchio',
        'Ontario Pinot Grigio',
        'Italy Pinot Grigio',
        'Ontario Sauvignon Blanc',
        'Germany Müller-Thurgau O/D'
      ],
      reds: [
        'Chile Malbec',
        'British Columbia Pinot Noir',
        'Okanagan Meritage',
        'Italy Nebbiolo',
        'Washington Merlot',
        'Australia Cabernet Sauvignon',
        'California Syrah Style',
        'California Cabernet Sauvignon',
        'California Zinfandel',
        'Chile Merlot Style'
      ]
    },
    {
      name: 'Gold',
      price: '$210',
      descriptor: "Winemaker's Collection",
      subtitle: 'Rich, expressive, elevated profiles',
      image: 'https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/gir44yyn_Website%20Wedding%20Pictures%20%288%29.svg',
      theme: 'from-yellow-600/20 to-yellow-800/10',
      accentColor: 'border-yellow-600/30',
      badgeColor: 'bg-yellow-600',
      whites: [
        'German Gewürztraminer',
        'Italy Bella Bianco O/D',
        'Argentina Trio: Viognier, Riesling, Chardonnay',
        'Italy Pinot Grigio',
        'Australia Chardonnay',
        'New Zealand Sauvignon Blanc',
        'Australia Viognier Pinot Gris'
      ],
      reds: [
        'France Merlot',
        'California Pinot Noir',
        'Chile Malbec',
        'Italy Sangiovese',
        'Australia Shiraz',
        'Amarone',
        'Australia Cabernet Sauvignon',
        'California Cabernet/Syrah/Zinfandel',
        'Italy Nebbiolo',
        'Italy Valpola'
      ]
    },
    {
      name: 'Platinum',
      price: '$230',
      descriptor: 'Reserve Series',
      subtitle: 'Bold, complex, top-tier wines',
      image: 'https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/s9yhbev2_Website%20Wedding%20Pictures%20%286%29.svg',
      theme: 'from-purple-800/20 to-purple-950/10',
      accentColor: 'border-purple-700/30',
      badgeColor: 'bg-purple-800',
      whites: [
        'Chile Chardonnay',
        "Winemaker's Trio White (Pinot Grigio/Sauv Blanc/Muscat)",
        'Italy Pinot Grigio',
        'South Africa Sauvignon Blanc'
      ],
      reds: [
        'Chile Merlot',
        'Italy Zinfandel',
        'Australia Cabernet Sauvignon',
        'Chile Carménère',
        'Australia Pinot Noir',
        'Australia Shiraz',
        'Spain Grenache Syrah',
        'Chile Malbec',
        'Italy Amarone',
        'Italy Rosso Grande Excellente',
        'Italy Super Tuscan',
        'Italy Zinfandel',
        "Winemaker's Trio Red (Cab Sauv/Syrah/Zinfandel)"
      ]
    }
  ];

  const fruitWines = {
    name: 'Fruit Wines',
    price: '$160',
    descriptor: 'Fresh & Vibrant',
    subtitle: 'Bright, fun, fruit-forward blends',
    image: 'https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/qzhsfozs_Website%20Wedding%20Pictures%20%287%29.svg',
    theme: 'from-pink-500/20 to-red-500/10',
    accentColor: 'border-pink-500/30',
    badgeColor: 'bg-gradient-to-r from-pink-500 to-red-500',
    varieties: [
      'Wild Watermelon',
      'Very Black Cherry',
      'Peach Perfection',
      'Green Apple Delight',
      'Mango Dragon Fruit Lemonade',
      'Tropical Lime',
      'Strawberry Sensation',
      'Seville Orange Sangria',
      "Rockin' Raspberry Rosé",
      'Blueberry Bliss',
      'Cranberry Craze',
      'Blackberry Blast',
      'Acai Raspberry Rapture'
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Sticky Intro Section */}
      <section className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              Your Wine, Your Way
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Craft premium wines at home with our expertly curated kits. Each makes 30 bottles of exceptional wine tailored to your taste.
            </p>
          </div>
        </div>
      </section>

      {/* Wine Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-16 sm:space-y-24">
        {/* Order Now Banner */}
        <section
          data-testid="wines-order-now-banner"
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FDB913] via-[#FFC940] to-[#FDB913] p-8 sm:p-10 shadow-2xl"
        >
          <div className="absolute -right-10 -top-10 opacity-10">
            <Wine className="w-48 h-48 text-gray-900" />
          </div>
          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Ready to Place Your Order?
              </h2>
              <p className="text-base sm:text-lg text-gray-800 max-w-2xl">
                Skip the call — select your wines online. We'll confirm, collect payment, and book your session.
              </p>
            </div>
            <Link to="/order" data-testid="wines-order-now-banner-cta">
              <Button
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white font-bold text-lg px-8 py-6 shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Order Now
              </Button>
            </Link>
          </div>
        </section>

        {categories.map((category, index) => (
          <section
            key={category.name}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={`transition-all duration-1000 ${
              visibleSections.includes(index)
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Category Header with Parallax Image */}
            <div className="relative h-48 sm:h-64 rounded-2xl overflow-hidden mb-8 shadow-xl">
              <div
                className="absolute inset-0 bg-cover bg-center transform scale-105 transition-transform duration-700 hover:scale-110"
                style={{
                  backgroundImage: `url(${category.image})`,
                  backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll'
                }}
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${category.theme} backdrop-blur-[1px]`} />
              <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
                <span className={`${category.badgeColor} text-white px-4 py-1 rounded-full text-sm font-semibold mb-3`}>
                  {category.descriptor}
                </span>
                <h2 className="text-4xl sm:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                  {category.name}
                </h2>
                <p className="text-2xl sm:text-3xl font-bold text-[#FDB913] mb-2 drop-shadow-md">
                  {category.price}
                </p>
                <p className="text-base sm:text-lg text-white/90 drop-shadow-md">
                  {category.subtitle}
                </p>
              </div>
            </div>

            {/* Two-Column Wine List */}
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* White Wines */}
              <Card className={`border-2 ${category.accentColor} hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Wine className="w-6 h-6 text-gray-600" />
                    <h3 className="text-2xl font-bold text-gray-900">White Wines</h3>
                  </div>
                  <div className="space-y-2">
                    {category.whites.map((wine, idx) => (
                      <div
                        key={idx}
                        className="group flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                      >
                        <div className={`w-2 h-2 ${category.badgeColor} rounded-full group-hover:scale-150 transition-transform`} />
                        <span className="text-gray-700 group-hover:text-gray-900 group-hover:font-medium transition-all">
                          {wine}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Red Wines */}
              <Card className={`border-2 ${category.accentColor} hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Wine className="w-6 h-6 text-red-700" />
                    <h3 className="text-2xl font-bold text-gray-900">Red Wines</h3>
                  </div>
                  <div className="space-y-2">
                    {category.reds.map((wine, idx) => (
                      <div
                        key={idx}
                        className="group flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-red-50 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-2 h-2 bg-red-700 rounded-full group-hover:scale-150 transition-transform" />
                        <span className="text-gray-700 group-hover:text-gray-900 group-hover:font-medium transition-all">
                          {wine}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Per-Tier Order CTA */}
            <div className="mt-6 flex justify-center">
              <Link to="/order" data-testid={`order-tier-${category.name.toLowerCase()}`}>
                <Button
                  className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold px-6 py-5 shadow-md transform hover:scale-105 transition-all"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Order {category.name} Wine — {category.price}
                </Button>
              </Link>
            </div>
          </section>
        ))}

        {/* Fruit Wines Section */}
        <section
          ref={(el) => (sectionRefs.current[categories.length] = el)}
          className={`transition-all duration-1000 ${
            visibleSections.includes(categories.length)
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Fruit Wines Header */}
          <div className="relative h-48 sm:h-64 rounded-2xl overflow-hidden mb-8 shadow-xl">
            <div
              className="absolute inset-0 bg-cover bg-center transform scale-105 transition-transform duration-700 hover:scale-110"
              style={{
                backgroundImage: `url(${fruitWines.image})`,
                backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll'
              }}
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${fruitWines.theme} backdrop-blur-[1px]`} />
            <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
              <span className={`${fruitWines.badgeColor} text-white px-4 py-1 rounded-full text-sm font-semibold mb-3`}>
                {fruitWines.descriptor}
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                {fruitWines.name}
              </h2>
              <p className="text-2xl sm:text-3xl font-bold text-[#FDB913] mb-2 drop-shadow-md">
                {fruitWines.price}
              </p>
              <p className="text-base sm:text-lg text-white/90 drop-shadow-md">
                {fruitWines.subtitle}
              </p>
            </div>
          </div>

          {/* Fruit Wines Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fruitWines.varieties.map((wine, idx) => (
              <div
                key={idx}
                className="group relative bg-gradient-to-br from-pink-50 to-red-50 border-2 border-pink-200 rounded-xl p-4 sm:p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-pink-500 group-hover:rotate-12 transition-transform" />
                  <span className="font-semibold text-gray-900 group-hover:text-pink-700 transition-colors">
                    {wine}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Fruit Wines Order CTA */}
          <div className="mt-6 flex justify-center">
            <Link to="/order" data-testid="order-tier-fruit">
              <Button
                className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold px-6 py-5 shadow-md transform hover:scale-105 transition-all"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Order Fruit Wine — {fruitWines.price}
              </Button>
            </Link>
          </div>
        </section>

        {/* Bottom Order Now CTA */}
        <section
          data-testid="wines-order-now-bottom"
          className="rounded-2xl bg-gradient-to-br from-[#FDB913] to-[#E5A50A] p-8 sm:p-12 text-center shadow-2xl"
        >
          <ShoppingCart className="w-16 h-16 mx-auto mb-6 text-gray-900" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
            Found Your Wine? Place Your Order
          </h2>
          <p className="text-lg sm:text-xl mb-8 text-gray-800 max-w-2xl mx-auto">
            Online ordering is fast & simple. Select your batches, half-batches, and let us know how you'd like to activate your yeast.
          </p>
          <Link to="/order" data-testid="wines-order-now-bottom-cta">
            <Button
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white font-semibold text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300 shadow-xl"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Start My Order
            </Button>
          </Link>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 sm:p-12 text-center text-white shadow-2xl">
          <Wine className="w-16 h-16 mx-auto mb-6 text-[#FDB913]" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Not Sure Which Wine to Choose?
          </h2>
          <p className="text-lg sm:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Our wine experts are here to help! Book a free consultation and we'll guide you to the perfect wine for your taste and occasion.
          </p>
          <Button
            size="lg"
            onClick={onBookCall}
            data-track="book_call:wines_book_call"
            className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300 shadow-xl"
          >
            <Phone className="w-5 h-5 mr-2" />
            Book Your Free Consultation
          </Button>
        </section>
      </div>
    </div>
  );
};

export default Wines;
